<div class="account-menu">

    <!-- Signing in: the page reloads in a moment; the menu says so instead of flashing the signed-in view first. -->
    <div class="account-menu__signing" *ngIf="signingIn" role="status" aria-live="polite">
        <span class="account-menu__spinner" aria-hidden="true"></span>
        <span>{{ 'signing-you-in' | t:'Signing you in…' }}</span>
    </div>

    <form class="account-menu__form" [formGroup]="loginFormMenu" (ngSubmit)="onSubmit($event)" *ngIf="isLoggedIn == false && !signingIn" novalidate>
        <div class="account-menu__form-title">{{ 'log-in-to-your-account' | t:'Log In to Your Account' }}</div>
        <div class="form-group">
            <label for="header-signin-email" class="sr-only">{{ 'email-address' | t:'Email address' }}</label>
            <input #emailBox id="header-signin-email" type="email" class="form-control form-control-sm" [attr.placeholder]="'email-address' | t:'Email address'"
                   formControlName="email" name="email" autocomplete="username" inputmode="email" autocapitalize="off" spellcheck="false">
        </div>
        <div class="form-group">
            <label for="header-signin-password" class="sr-only">{{ 'password' | t:'Password' }}</label>
            <div class="account-menu__form-forgot">
                <input #passwordBox id="header-signin-password" type="password" class="form-control form-control-sm" [attr.placeholder]="'password' | t:'Password'"
                       formControlName="password" name="password" autocomplete="current-password">
                <a routerLink="/account/forgot-password" class="account-menu__form-forgot-link" (click)="closeMenu.emit()">{{ 'forgot-password-short' | t:'Forgot?' }}</a>
            </div>
        </div>
        <div class="form-group account-menu__form-button">
            <button type="submit" class="btn btn-primary btn-sm" [ngClass]="{'btn-loading': isRequesting}" [disabled]="isRequesting || loginFormMenu.invalid">{{ 'login' | t:'Login' }}</button>
        </div>
        <!-- Same flow as the login page: one button that signs in or signs up, and
             links to an existing account when the address already has a password. -->
        <div class="account-menu__google">
            <div class="account-menu__google-divider"><span>{{ 'or' | t:'or' }}</span></div>
            <button type="button" class="btn btn-sm btn-light account-menu__google-btn"
                    [ngClass]="{'btn-loading': requestingGoogle}"
                    (click)="continueWithGoogle()">
                <svg class="account-menu__google-icon" viewBox="0 0 18 18" aria-hidden="true">
                    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.91c1.71-1.57 2.69-3.89 2.69-6.62z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 009 18z"/>
                    <path fill="#FBBC05" d="M3.96 10.71a5.41 5.41 0 010-3.42V4.96H.96a9 9 0 000 8.08l3-2.33z"/>
                    <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 00.96 4.96l3 2.33C4.67 5.16 6.65 3.58 9 3.58z"/>
                </svg>
                <span>{{ 'continue-with-google' | t:'Continue with Google' }}</span>
            </button>
        </div>
        <div class="account-menu__form-link"><a routerLink="/account/signup" (click)="closeMenu.emit()">{{ 'create-an-account' | t:'Create An Account' }}</a></div>
    </form>

    <ng-container *ngIf="isLoggedIn == true && !signingIn">
        <!-- Who is signed in, and as what. -->
        <a routerLink="/account/{{defaultRoute}}" class="account-menu__user" (click)="closeMenu.emit()">
            <div class="account-menu__user-avatar account-menu__initials" aria-hidden="true">{{ initials }}</div>
            <div class="account-menu__user-info">
                <div class="account-menu__user-name">{{ fullName }}</div>
                <div class="account-menu__user-email">{{ userInfo?.Email }}</div>
                <div class="account-menu__user-role">{{ isOwner ? ('account-role-owner' | t:'Site owner') : isSeller ? ('account-role-seller' | t:'Seller') : ('account-role-buyer' | t:'Buyer') }}</div>
            </div>
        </a>
        <div class="account-menu__divider"></div>

        <!-- The owner's console. -->
        <ul class="account-menu__links" *ngIf="isOwner">
            <li><a routerLink="/account/dashboard" (click)="closeMenu.emit()">{{ 'dashboard' | t:'Dashboard' }}</a></li>
            <li><a routerLink="/account/orders" (click)="closeMenu.emit()">{{ 'orders' | t:'Orders' }}</a></li>
            <li><a routerLink="/account/shop-requests" (click)="closeMenu.emit()">{{ 'shop-requests' | t:'Shop requests' }}</a></li>
            <li><a routerLink="/account/pending-products" (click)="closeMenu.emit()">{{ 'product-approval' | t:'Product approval' }}</a></li>
        </ul>

        <!-- A seller's console (the owner, 2026-11-02: never a shopper's pages). -->
        <ul class="account-menu__links" *ngIf="isSeller">
            <li><a routerLink="/account/shop/dashboard" (click)="closeMenu.emit()">{{ 'dashboard' | t:'Dashboard' }}</a></li>
            <li><a routerLink="/account/shop/orders" (click)="closeMenu.emit()">{{ 'orders' | t:'Orders' }}</a></li>
            <li><a [routerLink]="productsUrl" (click)="closeMenu.emit()">{{ 'products' | t:'Products' }}</a></li>
            <li><a routerLink="/account/chat" (click)="closeMenu.emit()">{{ 'chat-inbox' | t:'Inbox' }}</a></li>
        </ul>

        <!-- A buyer's own pages. -->
        <ul class="account-menu__links" *ngIf="!isOwner && !isSeller">
            <li><a routerLink="/account/orders" (click)="closeMenu.emit()">{{ 'order-history' | t:'Order History' }}</a></li>
            <li><a routerLink="/account/addresses" (click)="closeMenu.emit()">{{ 'addresses' | t:'Addresses' }}</a></li>
            <li><a routerLink="/account/wishlist" (click)="closeMenu.emit()">{{ 'wish-list' | t:'Wish List' }}</a></li>
        </ul>

        <div class="account-menu__divider"></div>
        <ul class="account-menu__links">
            <li><a routerLink="/account/myaccount" (click)="closeMenu.emit()">{{ 'my-profile' | t:'My Profile' }}</a></li>
            <li><a href="javascript:void(0)" role="button" (click)="logout()">{{ 'logout' | t:'Logout' }}</a></li>
        </ul>
    </ng-container>
</div>
